import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCabLandingPage } from '../../api/cabsPublic';
import { omitEmptyParams } from './helpers';
import CabsBreadcrumbs from './CabsBreadcrumbs';
import CabsSearchWidget from './CabsSearchWidget';
import VideoModal from './VideoModal';

const CabsLanding = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeVideo, setActiveVideo] = useState(null);
  const urlSyncFailedRef = useRef(false);

  const landingQuery = useQuery({
    queryKey: ['cab-landing-page'],
    queryFn: getCabLandingPage,
  });

  const landing = landingQuery.data?.data || landingQuery.data || {};
  const settings = landing.settings || {};
  const visibility = settings.sectionVisibility || {};
  const heroBanner = (landing.heroBanners || [])[0] || null;

  const initialValues = useMemo(() => {
    const tripTypeParam = searchParams.get('tripType');
    const tripType = tripTypeParam === 'roundtrip' ? 'ROUNDTRIP' : 'ONEWAY';
    return {
      tripType,
      pickupLocationId: searchParams.get('pickupLocationId') || '',
      dropLocationId: searchParams.get('dropLocationId') || '',
      pickupDate: searchParams.get('pickupDate') || '',
      pickupTime: searchParams.get('pickupTime') || '',
      returnDate: searchParams.get('returnDate') || '',
      passengers: Number(searchParams.get('passengers') || 1),
    };
  }, [searchParams]);

  const handleSearch = (values) => {
    const params = omitEmptyParams({
      tripType: values.tripType === 'ROUNDTRIP' ? 'roundtrip' : 'oneway',
      pickupLocationId: values.pickupLocationId,
      dropLocationId: values.dropLocationId,
      pickupDate: values.pickupDate,
      pickupTime: values.pickupTime,
      returnDate: values.returnDate,
      passengers: values.passengers,
    });
    const query = params.toString();
    navigate(query ? `/cabs/list?${query}` : '/cabs/list');
  };

  const handleValuesChange = (values) => {
    const params = omitEmptyParams({
      tripType: values.tripType === 'ROUNDTRIP' ? 'roundtrip' : 'oneway',
      pickupLocationId: values.pickupLocationId,
      dropLocationId: values.dropLocationId,
      pickupDate: values.pickupDate,
      pickupTime: values.pickupTime,
      returnDate: values.tripType === 'ROUNDTRIP' ? values.returnDate : '',
      passengers: values.passengers,
    });
    const nextQuery = params.toString();
    const currentQuery = searchParams.toString();
    if (urlSyncFailedRef.current || nextQuery === currentQuery) return;
    try {
      setSearchParams(params, { replace: true });
    } catch {
      urlSyncFailedRef.current = true;
    }
  };

  const heroBackground = heroBanner?.imageUrl || settings.heroBackgroundImageUrl || '';
  const vehicleCategories = landing.vehicleCategories || [];
  const guestStories = landing.guestStories || [];
  const travelGuides = landing.travelGuides || [];
  const promoVideos = landing.promoVideos || [];
  const popularPackages = landing.popularPackages || [];
  const popularRoutes = landing.popularRoutes || [];

  return (
    <section className="space-y-8">
      <CabsBreadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Cabs' }]} />

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div
          className="relative overflow-hidden rounded-[32px] border border-slate-200 shadow-[0_24px_70px_rgba(15,23,42,0.14)]"
          style={{
            backgroundImage: heroBackground
              ? `linear-gradient(135deg, rgba(8,26,53,0.92), rgba(13,124,117,0.72)), url(${heroBackground})`
              : 'linear-gradient(135deg, #081a35 0%, #0b5563 58%, #1c7c73 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="relative flex min-h-[360px] flex-col justify-between gap-6 p-6 text-white sm:min-h-[420px] sm:p-8">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#d7c08b]">
                {settings.heroEyebrow || 'Oxitaxi Cabs'}
              </p>
              <h1 className="mt-4 max-w-xl text-3xl font-semibold leading-tight sm:text-5xl">
                {heroBanner?.title || settings.heroTitle}
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-slate-100 sm:text-base">
                {heroBanner?.subtitle || settings.heroSubtitle}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to={settings.heroPrimaryButtonLink || heroBanner?.ctaLink || '/cabs/list'}
                className="rounded-full bg-[#d2a54b] px-5 py-2.5 text-sm font-semibold text-slate-950 no-underline"
              >
                {settings.heroPrimaryButtonText || heroBanner?.ctaText || 'Search rides'}
              </Link>
              <Link
                to="/cabs/packages"
                className="rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white no-underline"
              >
                Browse transport packages
              </Link>
            </div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white/95 p-4 shadow-[0_24px_60px_rgba(15,23,42,0.08)] sm:p-6">
          <div className="mb-4">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Trip Search</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">Plan a ride in minutes</h2>
            <p className="mt-2 text-sm text-slate-600">
              Search one-way rides, round trips, or move straight into curated transport packages.
            </p>
          </div>
          <CabsSearchWidget initialValues={initialValues} onSubmit={handleSearch} onValuesChange={handleValuesChange} />
        </div>
      </div>

      {visibility.vehicles !== false && (
        <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#0d6b66]">Fleet</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                {settings.vehicleSectionTitle || 'Vehicles for every journey'}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {settings.vehicleSectionSubtitle || 'Active vehicle categories are managed directly from the admin console.'}
              </p>
            </div>
            <Link to="/cabs/list" className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 no-underline">
              View fleet
            </Link>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {vehicleCategories.map((category) => (
              <Link
                key={category.id}
                to={`/cabs/list?categoryId=${encodeURIComponent(category.id)}`}
                className="group overflow-hidden rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f7faf9_100%)] shadow-sm no-underline"
              >
                <div className="h-44 overflow-hidden bg-slate-100">
                  {category.imageUrl ? (
                    <img src={category.imageUrl} alt={category.name} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-slate-400">No image</div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="line-clamp-2 text-base font-semibold text-slate-900">{category.name}</h3>
                      <p className="mt-2 line-clamp-2 text-sm text-slate-600">
                        {category.description || 'Premium category for outstation and point-to-point rides.'}
                      </p>
                    </div>
                    <span className="rounded-full bg-[#e8f7f3] px-2.5 py-1 text-[11px] font-semibold text-[#0d6b66]">
                      {category.tripCount || 0} vehicles
                    </span>
                  </div>
                </div>
              </Link>
            ))}
            {!vehicleCategories.length && (
              <div className="rounded-[24px] border border-dashed border-slate-200 p-6 text-sm text-slate-500">
                No active vehicle categories yet.
              </div>
            )}
          </div>
        </section>
      )}

      {visibility.guestStories !== false && (
        <section className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#0d6b66]">Stories</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">
              {settings.guestStoriesTitle || 'Guest trip highlights'}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {settings.guestStoriesSubtitle || 'Fresh trip inspiration from dynamic guest-story records.'}
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {guestStories.slice(0, 3).map((story) => (
              <article key={story.id} className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
                <div className="h-56 bg-slate-100">
                  {story.imageUrl ? (
                    <img src={story.imageUrl} alt={story.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-slate-400">No image</div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="line-clamp-2 text-lg font-semibold text-slate-900">{story.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{story.caption || story.location || 'Oxitaxi journey highlight.'}</p>
                  <p className="mt-3 text-xs uppercase tracking-[0.18em] text-slate-400">
                    {story.tripDate ? new Date(story.tripDate).toLocaleDateString() : story.location || 'Curated ride'}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {visibility.travelGuides !== false && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#0d6b66]">Guides</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                {settings.travelGuidesTitle || 'Travel guides & tips'}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {settings.travelGuidesSubtitle || 'Editorial reads pulled dynamically from the cab travel guide content.'}
              </p>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {travelGuides.slice(0, 4).map((guide) => (
              <article key={guide.id} className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
                <div className="h-40 bg-slate-100">
                  {guide.coverImageUrl ? (
                    <img src={guide.coverImageUrl} alt={guide.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-slate-400">No image</div>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                    {guide.publishedAt ? new Date(guide.publishedAt).toLocaleDateString() : 'Guide'}
                  </p>
                  <h3 className="mt-2 line-clamp-2 text-base font-semibold text-slate-900">{guide.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{guide.excerpt || 'Fresh travel planning insight.'}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {visibility.videos !== false && (
        <section className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#0d6b66]">Watch</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">
              {settings.videosTitle || 'Promotional videos'}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {settings.videosSubtitle || 'Preview the Oxitaxi experience through active promotional videos.'}
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {promoVideos.slice(0, 4).map((video) => (
              <button
                key={video.id}
                type="button"
                onClick={() => setActiveVideo(video)}
                className="overflow-hidden rounded-[24px] border border-slate-200 bg-white text-left shadow-sm"
              >
                <div className="relative h-48 bg-slate-100">
                  {video.thumbnailUrl ? (
                    <img src={video.thumbnailUrl} alt={video.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-slate-400">No thumbnail</div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-950/25">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-lg text-slate-900">
                      Play
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="line-clamp-2 text-base font-semibold text-slate-900">{video.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{video.caption || 'Tap to preview this video.'}</p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {visibility.popularPackages !== false && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#0d6b66]">Packages</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                {settings.popularPackagesTitle || 'Popular packages'}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {settings.popularPackagesSubtitle || 'Compact transport package cards generated from active packages and variants.'}
              </p>
            </div>
            <Link to="/cabs/packages/list" className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 no-underline">
              Browse packages
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {popularPackages.slice(0, 4).map((pkg) => (
              <article key={pkg.id} className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
                <div className="h-48 bg-slate-100">
                  {pkg.coverImageUrl ? (
                    <img src={pkg.coverImageUrl} alt={pkg.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-slate-400">No image</div>
                  )}
                </div>
                <div className="space-y-3 p-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                      {pkg.location?.name || 'Transport package'}
                    </p>
                    <h3 className="mt-2 line-clamp-2 text-lg font-semibold text-slate-900">{pkg.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-slate-600">{pkg.description || pkg.highlights || 'Custom transport itinerary.'}</p>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="rounded-full bg-[#edf7f5] px-3 py-1 text-[11px] font-semibold text-[#0d6b66]">
                      {pkg.days}D / {pkg.nights}N
                    </span>
                    <span className="font-semibold text-slate-900">
                      {pkg.startingFromPrice ? `From INR ${Number(pkg.startingFromPrice).toLocaleString()}` : 'Price on request'}
                    </span>
                  </div>
                  <Link
                    to={`/cabs/packages/${pkg.slug}`}
                    className="inline-flex rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white no-underline"
                  >
                    View details
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {visibility.popularRoutes !== false && (
        <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.24em] text-[#0d6b66]">Routes</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">
              {settings.popularRoutesTitle || 'Popular routes'}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {settings.popularRoutesSubtitle || 'Frequently booked routes grouped dynamically from the admin route builder.'}
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {popularRoutes.slice(0, 3).map((group) => (
              <div key={group.id || group.title} className="rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f7faf9_100%)] p-4">
                <h3 className="text-sm font-semibold text-slate-900">{group.title}</h3>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">
                  {group.fromLocation?.name || 'Pickup city'}
                </p>
                <div className="mt-4 space-y-2">
                  {(group.items || []).slice(0, 5).map((item) => {
                    const params = omitEmptyParams({
                      tripType: initialValues.tripType === 'ROUNDTRIP' ? 'roundtrip' : 'oneway',
                      pickupLocationId: group.fromLocation?.id,
                      dropLocationId: item.toLocationId,
                      pickupDate: initialValues.pickupDate,
                      pickupTime: initialValues.pickupTime,
                      returnDate: initialValues.returnDate,
                      passengers: initialValues.passengers,
                    });
                    return (
                      <Link
                        key={item.id}
                        to={`/cabs/list?${params.toString()}`}
                        className="flex items-center justify-between rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50"
                      >
                        <span>{item.name}</span>
                        <span className="text-xs text-slate-400">View</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {visibility.footerCta !== false && (
        <section className="overflow-hidden rounded-[32px] bg-[linear-gradient(135deg,#081a35_0%,#0b5563_55%,#0d6b66_100%)] px-6 py-8 text-white shadow-[0_24px_70px_rgba(8,26,53,0.24)] sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.24em] text-[#d7c08b]">Request Support</p>
              <h2 className="mt-2 text-2xl font-semibold">{settings.footerCtaTitle || 'Ready to plan your next ride?'}</h2>
              <p className="mt-3 text-sm text-slate-100">
                {settings.footerCtaSubtitle || 'Tell us your route and preferences. We will help you choose the best option.'}
              </p>
            </div>
            <Link
              to={settings.footerCtaButtonLink || '/cabs/list'}
              className="rounded-full bg-[#d2a54b] px-5 py-2.5 text-sm font-semibold text-slate-950 no-underline"
            >
              {settings.footerCtaButtonText || 'Request a quote'}
            </Link>
          </div>
        </section>
      )}

      <VideoModal show={Boolean(activeVideo)} onHide={() => setActiveVideo(null)} video={activeVideo} />
    </section>
  );
};

export default CabsLanding;
